/**
 * useInsightsData Hook
 * Manages data fetching for the Insights dashboard with proper loading/error states
 */

import { useState, useEffect, useCallback } from 'react';
import insightsService from '../services/insightsService';
import type { InsightsAgent } from '../services/insightsService';
import type {
  AgentStat,
  TenantRanking,
  TimeseriesPoint,
  TenantNode,
  InsightTemplate,
  InsightFilters,
  InsightsByTenantRow,
  InsightsByAgentRow,
} from '../types/insights';

/** Expand comma-separated chatbot IDs (from grouped agent options) into individual IDs */
function expandChatbotIds(ids: string[]): string[] {
  return ids.flatMap(id => id.split(','));
}

type ChatbotOption = InsightsAgent;

interface UseInsightsDataReturn {
  // Tenant tree (for scope selector)
  tenantTree: TenantNode[];
  tenantTreeLoading: boolean;

  // Chatbots (for scope selector, independent of stats data)
  chatbots: ChatbotOption[];
  chatbotsLoading: boolean;

  // Agent stats (filtered by scope)
  agentStats: AgentStat[];
  agentStatsLoading: boolean;
  agentStatsError: string | null;

  // Agent timeseries (filtered by scope)
  agentTimeseries: TimeseriesPoint[];
  agentTimeseriesLoading: boolean;

  // Global agent stats (date-only, no tenant/chatbot filter — for Agent Performance table)
  globalAgentStats: AgentStat[];
  globalAgentTimeseries: TimeseriesPoint[];
  globalAgentLoading: boolean;

  // Tenant rankings
  tenantRankings: TenantRanking[];
  tenantRankingsLoading: boolean;

  // Tenant timeseries
  tenantTimeseries: TimeseriesPoint[];
  tenantTimeseriesLoading: boolean;

  // Templates
  templates: InsightTemplate[];
  templatesLoading: boolean;
  templatesError: string | null;

  // All template aggregations (map from templateId → aggregation)

  // Insights-by-tenant / by-agent
  insightsByTenant: InsightsByTenantRow[];
  insightsByTenantLoading: boolean;
  insightsByAgent: InsightsByAgentRow[];
  insightsByAgentLoading: boolean;

  // Actions
  fetchAgentData: (filters: InsightFilters) => void;
  fetchGlobalAgentData: (filters: InsightFilters) => void;
  fetchTenantData: (filters: InsightFilters) => void;
  fetchTemplates: () => void;
  fetchInsightsBreakdown: (filters: InsightFilters) => void;
  refreshAll: (filters: InsightFilters) => void;
  reloadChatbots: (tenantIds?: string[]) => void;
}

export function useInsightsData(): UseInsightsDataReturn {
  const [tenantTree, setTenantTree] = useState<TenantNode[]>([]);
  const [tenantTreeLoading, setTenantTreeLoading] = useState(false);

  const [chatbots, setChatbots] = useState<ChatbotOption[]>([]);
  const [chatbotsLoading, setChatbotsLoading] = useState(false);

  const [agentStats, setAgentStats] = useState<AgentStat[]>([]);
  const [agentStatsLoading, setAgentStatsLoading] = useState(false);
  const [agentStatsError, setAgentStatsError] = useState<string | null>(null);

  const [agentTimeseries, setAgentTimeseries] = useState<TimeseriesPoint[]>([]);
  const [agentTimeseriesLoading, setAgentTimeseriesLoading] = useState(false);

  const [globalAgentStats, setGlobalAgentStats] = useState<AgentStat[]>([]);
  const [globalAgentTimeseries, setGlobalAgentTimeseries] = useState<TimeseriesPoint[]>([]);
  const [globalAgentLoading, setGlobalAgentLoading] = useState(false);

  const [tenantRankings, setTenantRankings] = useState<TenantRanking[]>([]);
  const [tenantRankingsLoading, setTenantRankingsLoading] = useState(false);

  const [tenantTimeseries, setTenantTimeseries] = useState<TimeseriesPoint[]>([]);
  const [tenantTimeseriesLoading, setTenantTimeseriesLoading] = useState(false);

  const [templates, setTemplates] = useState<InsightTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState<string | null>(null);


  const [insightsByTenant, setInsightsByTenant] = useState<InsightsByTenantRow[]>([]);
  const [insightsByTenantLoading, setInsightsByTenantLoading] = useState(false);
  const [insightsByAgent, setInsightsByAgent] = useState<InsightsByAgentRow[]>([]);
  const [insightsByAgentLoading, setInsightsByAgentLoading] = useState(false);

  // Load tenant tree once on mount
  useEffect(() => {
    const loadTenantTree = async () => {
      setTenantTreeLoading(true);
      try {
        const tree = await insightsService.getTenantTree();
        setTenantTree(tree);
      } catch (error) {
        console.error('[useInsightsData] Failed to load tenant tree:', error);
      } finally {
        setTenantTreeLoading(false);
      }
    };

    loadTenantTree();
  }, []);

  const reloadChatbots = useCallback(async (tenantIds?: string[]) => {
    setChatbotsLoading(true);
    try {
      const data = await insightsService.getChatbots(tenantIds ? { tenant_ids: tenantIds } : {});
      // Deduplicate by chatbot_id
      const seen = new Set<string>();
      const unique = data.filter(c => {
        if (seen.has(c.chatbot_id)) return false;
        seen.add(c.chatbot_id);
        return true;
      });
      setChatbots(unique);
    } catch (error) {
      console.error('[useInsightsData] Failed to load chatbots:', error);
    } finally {
      setChatbotsLoading(false);
    }
  }, []);

  // Load chatbots once on mount
  useEffect(() => {
    reloadChatbots();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGlobalAgentData = useCallback(async (filters: InsightFilters) => {
    setGlobalAgentLoading(true);
    try {
      const params = {
        date_from: filters.dateFrom,
        date_to: filters.dateTo,
        tenant_ids: filters.tenantIds.length > 0 ? filters.tenantIds : undefined,
        chatbot_ids: filters.chatbotIds.length > 0 ? expandChatbotIds(filters.chatbotIds) : undefined,
      };
      const [stats, timeseries] = await Promise.all([
        insightsService.getAgentStats(params),
        insightsService.getAgentTimeseries(params),
      ]);
      setGlobalAgentStats(stats);
      setGlobalAgentTimeseries(timeseries);
    } catch (error) {
      console.error('[useInsightsData] Failed to load global agent data:', error);
    } finally {
      setGlobalAgentLoading(false);
    }
  }, []);

  const fetchAgentData = useCallback(async (filters: InsightFilters) => {
    setAgentStatsLoading(true);
    setAgentTimeseriesLoading(true);
    setAgentStatsError(null);

    try {
      const params = {
        date_from: filters.dateFrom,
        date_to: filters.dateTo,
        tenant_ids: filters.tenantIds.length > 0 ? filters.tenantIds : undefined,
        chatbot_ids: filters.chatbotIds.length > 0 ? expandChatbotIds(filters.chatbotIds) : undefined,
      };

      const [stats, timeseries] = await Promise.all([
        insightsService.getAgentStats(params),
        insightsService.getAgentTimeseries(params),
      ]);

      setAgentStats(stats);
      setAgentTimeseries(timeseries);
    } catch (error) {
      console.error('[useInsightsData] Failed to load agent data:', error);
      setAgentStatsError('Failed to load agent statistics');
    } finally {
      setAgentStatsLoading(false);
      setAgentTimeseriesLoading(false);
    }
  }, []);

  const fetchTenantData = useCallback(async (filters: InsightFilters) => {
    setTenantRankingsLoading(true);
    setTenantTimeseriesLoading(true);

    try {
      const params = {
        date_from: filters.dateFrom,
        date_to: filters.dateTo,
        tenant_ids: filters.tenantIds.length > 0 ? filters.tenantIds : undefined,
        chatbot_ids: filters.chatbotIds.length > 0 ? expandChatbotIds(filters.chatbotIds) : undefined,
      };

      const [rankings, timeseries] = await Promise.all([
        insightsService.getTenantRankings(params),
        insightsService.getTenantTimeseries(params),
      ]);

      setTenantRankings(rankings);
      setTenantTimeseries(timeseries);
    } catch (error) {
      console.error('[useInsightsData] Failed to load tenant data:', error);
    } finally {
      setTenantRankingsLoading(false);
      setTenantTimeseriesLoading(false);
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    setTemplatesError(null);

    try {
      const data = await insightsService.getTemplates(true); // always include hidden so management tab can show/unhide them
      setTemplates(data);
    } catch (error) {
      console.error('[useInsightsData] Failed to load templates:', error);
      setTemplatesError('Failed to load templates');
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  const fetchInsightsBreakdown = useCallback(async (filters: InsightFilters) => {
    setInsightsByTenantLoading(true);
    setInsightsByAgentLoading(true);
    const params = {
      date_from: filters.dateFrom,
      date_to: filters.dateTo,
      tenant_ids: filters.tenantIds.length > 0 ? filters.tenantIds : undefined,
      chatbot_ids: filters.chatbotIds.length > 0 ? expandChatbotIds(filters.chatbotIds) : undefined,
    };
    try {
      const [byTenant, byAgent] = await Promise.all([
        insightsService.getInsightsByTenant(params),
        insightsService.getInsightsByAgent(params),
      ]);
      setInsightsByTenant(byTenant);
      setInsightsByAgent(byAgent);
    } catch (error) {
      console.error('[useInsightsData] Failed to load insights breakdown:', error);
    } finally {
      setInsightsByTenantLoading(false);
      setInsightsByAgentLoading(false);
    }
  }, []);

  const refreshAll = useCallback(
    (filters: InsightFilters) => {
      fetchAgentData(filters);
      fetchGlobalAgentData(filters);
      fetchTenantData(filters);
      fetchInsightsBreakdown(filters);
    },
    [fetchAgentData, fetchGlobalAgentData, fetchTenantData, fetchInsightsBreakdown]
  );

  return {
    tenantTree,
    tenantTreeLoading,
    chatbots,
    chatbotsLoading,
    agentStats,
    agentStatsLoading,
    agentStatsError,
    agentTimeseries,
    agentTimeseriesLoading,
    tenantRankings,
    tenantRankingsLoading,
    tenantTimeseries,
    tenantTimeseriesLoading,
    templates,
    templatesLoading,
    templatesError,
    insightsByTenant,
    insightsByTenantLoading,
    insightsByAgent,
    insightsByAgentLoading,
    globalAgentStats,
    globalAgentTimeseries,
    globalAgentLoading,
    fetchAgentData,
    fetchGlobalAgentData,
    fetchTenantData,
    fetchTemplates,
    fetchInsightsBreakdown,
    refreshAll,
    reloadChatbots,
  };
}
